// ── BCH Rooms — native peer-to-peer audio rooms ───────────────────
//
// Rooms run entirely in the browser: audio is a WebRTC mesh between
// participants (src/lib/roomAudio.ts), signalling rides on Nostr
// (useRoomSignal), and the roster comes from NIP-53 kind 10312 presence
// (useRoomPresence). There is no audio server and no third party.
//
// Standard kinds throughout, so our rooms are visible to other NIP-53
// clients: 30312 for the room, 10312 for presence + raised hands, 1311
// for chat (reusing components/live/LiveChatPanel verbatim).
//
// ── The honest-roster rule ───────────────────────────────────────
//
// A grid that shows people who already left reads as broken faster than
// a missing feature does. So the avatar grid is driven by *presence*
// (which expires on its own), never by WebRTC join/leave alone — those
// messages never arrive when a laptop lid closes. Connection state is
// then layered on top per participant, and a peer that cannot connect
// says so instead of spinning forever.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Mic2, Mic, MicOff, Users, Plus, RefreshCw, ArrowLeft, Hand, LogOut,
  Info, Radio, MessageCircle, AlertTriangle, Loader2, Volume2,
} from 'lucide-react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { useRooms } from '../hooks/useRooms';
import { useRoomPresence } from '../hooks/useRoomPresence';
import { useRoomSignal } from '../hooks/useRoomSignal';
import { useWakeLock } from '../hooks/useWakeLock';
import { useNostrPublish } from '../hooks/useNostrPublish';
import LiveChatPanel from '../components/live/LiveChatPanel';
import BCHTipButton from '../components/BCHTipButton';
import VerifiedBadge from '../components/VerifiedBadge';
import {
  ROOM_KIND, roomCoordinate, parseRoomCoordinate, slugifyRoom,
  isValidRoomSlug, hasTurn, micBlocker,
} from '../lib/rooms';
import type { LiveRoom } from '../hooks/useRooms';
import {
  createMeshTransport, type RoomAudioTransport, type RoomParticipant,
  type RoomRole, type SignalPayload, type PeerStatus,
} from '../lib/roomAudio';

// ══════════════════════════════════════════════════════════════════
//  PAGE
// ══════════════════════════════════════════════════════════════════

export default function BCHRoomsPage() {
  const [searchParams] = useSearchParams();
  const coordinate = searchParams.get('room') || '';
  const parsed = coordinate ? parseRoomCoordinate(coordinate) : null;

  if (parsed) {
    return <RoomView coordinate={coordinate} host={parsed.host} slug={parsed.slug} />;
  }
  return <RoomDirectory />;
}

// ══════════════════════════════════════════════════════════════════
//  DIRECTORY
// ══════════════════════════════════════════════════════════════════

function RoomDirectory() {
  const navigate = useNavigate();
  const { rooms, liveRooms, loading, refetch } = useRooms();
  const [showCreate, setShowCreate] = useState(false);

  const quiet = rooms.filter(r => r.participants === 0).slice(0, 12);

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Mic2 className="w-5 h-5 text-primary" /> BCH Rooms
            </h1>
            {liveRooms.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded-full font-bold animate-pulse">
                {liveRooms.length} LIVE
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowCreate(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" /> Start a Room
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Peer-to-peer audio — no server, no middleman. Tip speakers in BCH.
        </p>
      </div>

      {showCreate && <CreateRoomForm onClose={() => setShowCreate(false)} />}

      <div className="p-4 space-y-6">
        {liveRooms.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-red-500 flex items-center gap-1.5 mb-3">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Live Now
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveRooms.map(room => (
                <RoomCard
                  key={room.coordinate}
                  room={room}
                  onOpen={() => navigate(`/rooms?room=${encodeURIComponent(room.coordinate)}`)}
                />
              ))}
            </div>
          </section>
        )}

        {quiet.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Quiet Rooms
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quiet.map(room => (
                <RoomCard
                  key={room.coordinate}
                  room={room}
                  onOpen={() => navigate(`/rooms?room=${encodeURIComponent(room.coordinate)}`)}
                />
              ))}
            </div>
          </section>
        )}

        {!loading && rooms.length === 0 && (
          <div className="text-center py-16">
            <Mic2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <h3 className="font-semibold mb-2">No rooms yet</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Start the first BCH Room and invite people to talk.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Start a Room
            </button>
          </div>
        )}

        {loading && rooms.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}

function RoomCard({ room, onOpen }: { room: LiveRoom; onOpen: () => void }) {
  const { profile } = useNostrProfile(room.host);
  const live = room.participants > 0;
  const hostName = profile?.display_name || profile?.name || room.host.slice(0, 8);

  return (
    <button
      onClick={onOpen}
      className={`w-full text-left glass-card overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
        live ? 'hover:border-red-500/30 hover:shadow-red-500/10' : 'hover:shadow-muted/10'
      }`}
    >
      {live && <div className="h-1 bg-gradient-to-r from-red-500 via-orange-400 to-amber-400 -m-4 mb-3" />}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg shrink-0 bg-primary/10 flex items-center justify-center overflow-hidden">
          {room.image
            ? <img src={room.image} alt="" className="w-full h-full object-cover" loading="lazy" />
            : <Mic2 className="w-5 h-5 text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold truncate">{room.title}</h3>
            {live && (
              <span className="shrink-0 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold animate-pulse flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
              </span>
            )}
          </div>
          {room.summary && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{room.summary}</p>
          )}
          <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
            <span className="truncate">{hostName}</span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {room.participants > 0 ? `${room.participants} in room` : 'empty'}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════
//  CREATE ROOM
// ══════════════════════════════════════════════════════════════════

function CreateRoomForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');

  const slug = slugifyRoom(title);
  const canCreate = isValidRoomSlug(slug) && !!user;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate || !user) return;
    setPublishing(true);
    setError('');
    const now = Math.floor(Date.now() / 1000);
    try {
      // NIP-53 meeting space. `service` points back at this app so other
      // clients can route users to the room rather than guess.
      await publish({
        kind: ROOM_KIND,
        content: '',
        tags: [
          ['d', slug],
          ['room', title.trim()],
          ['summary', description.trim()],
          ['status', 'open'],
          ['service', `${window.location.origin}/rooms?room=${roomCoordinate(user.pubkey, slug)}`],
          ['p', user.pubkey, '', 'Owner'],
          ['t', 'bchnostr'],
          ['t', 'audiospace'],
        ],
        created_at: now,
      });
      onClose();
      navigate(`/rooms?room=${encodeURIComponent(roomCoordinate(user.pubkey, slug))}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the room.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="border-b border-border p-4 bg-muted/30">
      <div className="max-w-lg mx-auto space-y-3">
        <div className="h-1.5 bg-gradient-to-r from-primary via-emerald-400 to-teal-500 rounded-full -mx-4 -mt-4 mb-4" />
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Mic2 className="w-4 h-4 text-primary" /> Start a BCH Room
        </h2>

        {!user && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Log in to start a room.
          </p>
        )}

        <form onSubmit={handleCreate} className="space-y-3">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="What's this room about?"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
            maxLength={100}
            required
          />
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Tell people what you'll be talking about..."
            className="w-full bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none resize-none"
            rows={2}
            maxLength={300}
          />

          <div className="rounded-lg border bg-muted/30 p-3 space-y-1">
            <h4 className="text-xs font-semibold flex items-center gap-1.5">
              <Info className="w-3 h-3 text-blue-500" /> How it works
            </h4>
            <ul className="text-[11px] text-muted-foreground space-y-0.5 list-disc list-inside">
              <li>Audio is peer-to-peer — no server sees or stores it</li>
              <li>Your room is announced on Nostr (NIP-53) so anyone can find it</li>
              <li>Listeners can tip you in BCH while you talk</li>
              <li>Best for small rooms; your mic is only used once you speak</li>
            </ul>
          </div>

          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}

          <button
            type="submit"
            disabled={publishing || !canCreate}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {publishing
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Radio className="w-4 h-4" />}
            {publishing ? 'Creating...' : 'Create Room'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  ROOM VIEW
// ══════════════════════════════════════════════════════════════════

function RoomView({ coordinate, host, slug }: { coordinate: string; host: string; slug: string }) {
  const navigate = useNavigate();
  const { user, setShowLoginDialog } = useAppContext();
  const { rooms } = useRooms();
  const { profile: hostProfile } = useNostrProfile(host);

  const room = rooms.find(r => r.coordinate === coordinate) || null;
  const hostName = hostProfile?.display_name || hostProfile?.name || host.slice(0, 8);
  const isHost = user?.pubkey === host;

  // ── Local session state ───────────────────────────────────────
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [muted, setMuted] = useState(true);
  const [handRaised, setHandRaised] = useState(false);
  const [error, setError] = useState('');
  const [peers, setPeers] = useState<RoomParticipant[]>([]);
  const [showChat, setShowChat] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);

  const transportRef = useRef<RoomAudioTransport | null>(null);

  // Keep the phone screen awake while in a room: a locked screen can
  // suspend the tab, killing both the audio and the heartbeat.
  useWakeLock(joined);

  // Presence drives the roster; it keeps working even when a peer's
  // WebRTC connection never establishes.
  const { present } = useRoomPresence({
    coordinate,
    selfPubkey: user?.pubkey,
    publishing: joined,
    handRaised,
  });

  // Signalling — only subscribed while in the room.
  const handleSignal = useCallback((payload: SignalPayload) => {
    void transportRef.current?.handleSignal(payload);
  }, []);

  const { sendSignal } = useRoomSignal({
    coordinate,
    selfPubkey: user?.pubkey,
    enabled: joined,
    onSignal: handleSignal,
  });

  const sendSignalRef = useRef(sendSignal);
  useEffect(() => { sendSignalRef.current = sendSignal; }, [sendSignal]);

  // Kind 25050 is ephemeral, so a `join` broadcast before our subscription
  // opened is simply gone — relays never replay it. Re-announce shortly
  // after joining, by which time the sockets are up, so existing
  // participants reliably learn about us and offer.
  useEffect(() => {
    if (!joined) return;
    const timers = [400, 1500, 4000].map(delay =>
      setTimeout(() => { void transportRef.current?.announce(); }, delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [joined]);

  // ── Join / leave ──────────────────────────────────────────────

  const join = useCallback(async (role: RoomRole) => {
    if (!user) { setShowLoginDialog(true); return; }
    setJoining(true);
    setError('');

    const transport = createMeshTransport({
      selfPubkey: user.pubkey,
      // Route through a ref so the transport always uses the current
      // sender without being rebuilt (which would drop the mesh).
      sendSignal: (to, payload) => sendSignalRef.current(to, payload),
      onParticipantsChanged: setPeers,
      onError: message => setError(message),
      onAudioBlocked: () => setAudioBlocked(true),
    });
    transportRef.current = transport;

    try {
      await transport.join(role);
      // Speakers start muted: nobody should be broadcast live the instant
      // they open a room.
      transport.setMuted(true);
      setMuted(true);
      setJoined(true);
    } catch {
      setError('Could not join the room.');
      transport.destroy();
      transportRef.current = null;
    } finally {
      setJoining(false);
    }
  }, [user, setShowLoginDialog]);

  const leave = useCallback(() => {
    const transport = transportRef.current;
    if (transport) {
      transport.leave();
      transport.destroy();
      transportRef.current = null;
    }
    setJoined(false);
    setPeers([]);
    setHandRaised(false);
    setMuted(true);
    setAudioBlocked(false);
  }, []);

  const enableSound = useCallback(async () => {
    await transportRef.current?.resumeAudio();
    setAudioBlocked(false);
  }, []);

  // Tear down on unmount / navigation so the mic is always released.
  useEffect(() => () => {
    const transport = transportRef.current;
    if (transport) {
      transport.leave();
      transport.destroy();
      transportRef.current = null;
    }
  }, []);

  // Drop peers whose presence has aged out. Without this a crashed tab's
  // peer entry lingers in the mesh forever, since `leave` never arrived.
  useEffect(() => {
    if (!joined) return;
    const transport = transportRef.current;
    if (!transport) return;
    const live = new Set(present.map(p => p.pubkey));
    for (const peer of peers) {
      if (peer.pubkey !== user?.pubkey && !live.has(peer.pubkey)) {
        transport.removePeer(peer.pubkey);
      }
    }
  }, [present, peers, joined, user?.pubkey]);

  const toggleMute = useCallback(() => {
    const transport = transportRef.current;
    if (!transport) return;
    const next = !muted;
    transport.setMuted(next);
    setMuted(next);
  }, [muted]);

  const toggleHand = useCallback(() => {
    const next = !handRaised;
    setHandRaised(next);
    transportRef.current?.setHandRaised(next);
  }, [handRaised]);

  // ── Roster ────────────────────────────────────────────────────
  //
  // Presence is the source of truth for *who* is here; the transport
  // supplies live audio state for those it has a connection to.
  const roster = useMemo(() => {
    const byPubkey = new Map(peers.map(p => [p.pubkey, p]));
    const seen = new Set<string>();
    const list: RosterEntry[] = [];

    for (const entry of present) {
      seen.add(entry.pubkey);
      const peer = byPubkey.get(entry.pubkey);
      const isSelf = entry.pubkey === user?.pubkey;
      list.push({
        pubkey: entry.pubkey,
        isSelf,
        // Hand state: trust our own local flag, else the presence event
        // (which is signed by that user and works without a connection).
        handRaised: isSelf ? handRaised : entry.handRaised || peer?.handRaised || false,
        muted: isSelf ? muted : peer?.muted ?? true,
        // The transport reports speaking for self and remote peers alike
        // (it analyses the local mic and every inbound stream).
        speaking: peer?.speaking ?? false,
        status: isSelf ? 'connected' : peer?.status ?? 'connecting',
        role: peer?.role ?? (entry.pubkey === host ? 'speaker' : 'listener'),
      });
    }

    // Anyone we have a live connection to but no presence for yet — they
    // just joined and their first heartbeat is still in flight.
    for (const peer of peers) {
      if (seen.has(peer.pubkey)) continue;
      list.push({
        pubkey: peer.pubkey,
        isSelf: peer.pubkey === user?.pubkey,
        handRaised: peer.handRaised,
        muted: peer.muted,
        speaking: peer.speaking,
        status: peer.status,
        role: peer.role,
      });
    }

    // Host first, then raised hands, then everyone else.
    return list.sort((a, b) => {
      if ((a.pubkey === host) !== (b.pubkey === host)) return a.pubkey === host ? -1 : 1;
      if (a.handRaised !== b.handRaised) return a.handRaised ? -1 : 1;
      return 0;
    });
  }, [present, peers, user?.pubkey, handRaised, muted, host]);

  const raisedHands = roster.filter(r => r.handRaised && !r.isSelf);
  const failedCount = roster.filter(r => r.status === 'failed').length;
  // Computed once per render rather than memoised: it only reads UA and
  // display-mode, both of which are stable for the life of the page.
  const micBlock = micBlocker();

  return (
    // Layout's <main> already reserves 5rem at the bottom for the fixed
    // mobile nav, and there is a ~3.5rem mobile header above. Sizing to
    // 100dvh here would push the controls (mute / leave) under the nav on
    // a phone, so the room is sized to what is actually visible.
    <div className="flex flex-col h-[calc(100dvh-8.5rem)] lg:h-[calc(100dvh-2rem)]">
      {/* Header */}
      <div className="shrink-0 bg-background/80 backdrop-blur-md border-b border-border px-4 py-2.5 flex items-center gap-2">
        <button
          onClick={() => navigate('/rooms')}
          className="-ml-1 rounded-lg p-1 hover:bg-muted"
          aria-label="Back to BCH Rooms"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold truncate flex items-center gap-2">
            {room?.title || slug}
            {roster.length > 0 && (
              <span className="shrink-0 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
                LIVE
              </span>
            )}
          </h1>
          <p className="text-[11px] text-muted-foreground truncate">
            Hosted by {hostName} · {roster.length} {roster.length === 1 ? 'person' : 'people'}
          </p>
        </div>
        <button
          onClick={() => setShowChat(v => !v)}
          className={`p-2 rounded-lg border border-border hover:bg-muted ${showChat ? 'text-primary' : 'text-muted-foreground'}`}
          aria-label="Toggle chat"
        >
          <MessageCircle className="w-4 h-4" />
        </button>
        {!isHost && (
          <BCHTipButton
            recipientPubkey={host}
            recipientProfile={hostProfile}
            dropdownAlign="right"
          />
        )}
      </div>

      {/* Autoplay was blocked — without this prompt the user just hears
          silence and assumes the room is broken. Needs a real tap. */}
      {joined && audioBlocked && (
        <button
          onClick={() => { void enableSound(); }}
          className="shrink-0 w-full bg-primary text-primary-foreground px-4 py-2.5 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-primary/90"
        >
          <Volume2 className="w-4 h-4" /> Tap to enable sound
        </button>
      )}

      {/* Connectivity warning — see lib/rooms.ts on why TURN matters. */}
      {joined && failedCount > 0 && (
        <div className="shrink-0 bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-700 dark:text-amber-400">
            Couldn't connect to {failedCount} {failedCount === 1 ? 'person' : 'people'}.
            {hasTurn()
              // With a relay available, a failure is a one-off rather than
              // the expected outcome — don't blame the network by default.
              ? ' They may have lost connection or closed the app.'
              : ' Peer-to-peer audio can\'t get through some mobile and office networks.'}
          </p>
        </div>
      )}

      {error && (
        <div className="shrink-0 bg-red-500/10 border-b border-red-500/30 px-4 py-2 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="flex-1 min-h-0 flex">
        {/* Stage */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          {roster.length === 0 && (
            <div className="text-center py-16 px-4">
              <Mic2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <h3 className="font-semibold mb-1">Nobody here yet</h3>
              <p className="text-muted-foreground text-sm">
                {joined ? 'Waiting for others to join…' : 'Join to open the room.'}
              </p>
            </div>
          )}

          {roster.length > 0 && (
            <div className="p-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {roster.map(entry => (
                  <ParticipantTile
                    key={entry.pubkey}
                    entry={entry}
                    isRoomHost={entry.pubkey === host}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Raised hands, for the host */}
          {isHost && raisedHands.length > 0 && (
            <div className="mx-4 mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
              <h3 className="text-xs font-semibold flex items-center gap-1.5 mb-2">
                <Hand className="w-3.5 h-3.5 text-amber-500" />
                {raisedHands.length} {raisedHands.length === 1 ? 'hand' : 'hands'} raised
              </h3>
              <div className="flex flex-wrap gap-2">
                {raisedHands.map(r => (
                  <RaisedHandChip key={r.pubkey} pubkey={r.pubkey} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chat */}
        {showChat && (
          <div className="w-full max-w-sm border-l border-border hidden md:flex md:flex-col min-h-0">
            <LiveChatPanel
              aTag={coordinate}
              creatorPubkey={host}
              className="flex-1 min-h-0"
            />
          </div>
        )}
      </div>

      {/* Chat on mobile — full width below the stage */}
      {showChat && (
        <div className="md:hidden shrink-0 h-72 border-t border-border flex flex-col min-h-0">
          <LiveChatPanel
            aTag={coordinate}
            creatorPubkey={host}
            className="flex-1 min-h-0"
          />
        </div>
      )}

      {/* Controls */}
      <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur-md px-4 py-3 safe-area-bottom">
        {!joined ? (
          <div className="space-y-2">
            {/* Tell people the mic won't work *before* they tap it. */}
            {micBlock && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>{micBlock.reason} {micBlock.hint}</span>
              </p>
            )}
            <div className="flex items-center gap-2">
              {!micBlock && (
                <button
                  onClick={() => join('speaker')}
                  disabled={joining}
                  className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {joining
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Mic className="w-4 h-4" />}
                  {joining ? 'Joining…' : 'Join with mic'}
                </button>
              )}
              <button
                onClick={() => join('listener')}
                disabled={joining}
                className={`px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 ${
                  micBlock
                    ? 'flex-1 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2'
                    : 'border border-border hover:bg-muted'
                }`}
              >
                {micBlock && <Volume2 className="w-4 h-4" />}
                {micBlock ? 'Join to listen' : 'Listen only'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className={`flex-1 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${
                muted
                  ? 'bg-muted text-foreground hover:bg-muted/80'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              {muted ? 'Unmute' : 'Mute'}
            </button>
            <button
              onClick={toggleHand}
              className={`px-4 py-2.5 rounded-lg border font-semibold text-sm flex items-center gap-2 transition-colors ${
                handRaised
                  ? 'border-amber-500 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                  : 'border-border hover:bg-muted'
              }`}
              aria-pressed={handRaised}
            >
              <Hand className="w-4 h-4" />
              <span className="hidden sm:inline">{handRaised ? 'Lower' : 'Raise'}</span>
            </button>
            <button
              onClick={leave}
              className="px-4 py-2.5 rounded-lg border border-red-500/40 text-red-500 font-semibold text-sm hover:bg-red-500/10 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Leave</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Participant tile ──────────────────────────────────────────────

interface RosterEntry {
  pubkey: string;
  isSelf: boolean;
  handRaised: boolean;
  muted: boolean;
  speaking: boolean;
  status: PeerStatus;
  role: RoomRole;
}

function ParticipantTile({ entry, isRoomHost }: { entry: RosterEntry; isRoomHost: boolean }) {
  const { profile } = useNostrProfile(entry.pubkey);
  const name = profile?.display_name || profile?.name || entry.pubkey.slice(0, 8);
  const picture = typeof profile?.picture === 'string' ? profile.picture : '';

  return (
    <div className="flex flex-col items-center text-center gap-1.5">
      <div className="relative">
        <div
          className={`w-16 h-16 rounded-full overflow-hidden bg-muted transition-all ${
            entry.speaking
              ? 'ring-[3px] ring-primary ring-offset-2 ring-offset-background'
              : 'ring-1 ring-border'
          } ${entry.status === 'failed' ? 'opacity-40 grayscale' : ''}`}
        >
          {picture
            ? <img src={picture} alt="" className="w-full h-full object-cover" loading="lazy" />
            : (
              <div className="w-full h-full flex items-center justify-center text-lg font-semibold text-muted-foreground">
                {name.slice(0, 1).toUpperCase()}
              </div>
            )}
        </div>

        {/* Mic state */}
        <div
          className={`absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full flex items-center justify-center border-2 border-background ${
            entry.muted ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground'
          }`}
        >
          {entry.muted ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
        </div>

        {/* Raised hand */}
        {entry.handRaised && (
          <div className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center border-2 border-background">
            <Hand className="w-3 h-3" />
          </div>
        )}

        {/* Still connecting */}
        {entry.status === 'connecting' && !entry.isSelf && (
          <div className="absolute inset-0 rounded-full bg-background/50 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="min-w-0 w-full">
        <p className="text-[11px] font-medium truncate flex items-center justify-center gap-0.5">
          <Link
            to={`/profile/${entry.pubkey}`}
            className="truncate hover:underline no-navigate"
          >
            {entry.isSelf ? 'You' : name}
          </Link>
          <VerifiedBadge pubkey={entry.pubkey} nip05={profile?.nip05} size="xs" />
        </p>
        {isRoomHost && (
          <p className="text-[10px] text-primary font-semibold">Host</p>
        )}
        {entry.status === 'failed' && (
          <p className="text-[10px] text-amber-600 dark:text-amber-500">can't connect</p>
        )}
      </div>
    </div>
  );
}

function RaisedHandChip({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.display_name || profile?.name || pubkey.slice(0, 8);
  return (
    <span className="text-[11px] px-2 py-1 rounded-full bg-background border border-border">
      {name}
    </span>
  );
}
